import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * A language-only selector for the header: a button that opens a plain list of the
 * shop's languages.
 *
 * This component only opens and closes the list. Choosing a language is done by the
 * submit buttons inside Shopify's own localization form, each carrying
 * `name="language_code"`, so the switch itself is the platform's behaviour rather than
 * a hand-rolled redirect.
 *
 * @typedef {object} LanguageSelectorRefs
 * @property {HTMLButtonElement} button - The button that opens the list.
 * @property {HTMLElement} panel - The list of languages.
 *
 * @extends {Component<LanguageSelectorRefs>}
 */
class LanguageSelectorComponent extends Component {
  requiredRefs = ['button', 'panel'];

  get isOpen() {
    return !this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Opens the list, or closes it when it is already open.
   */
  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  open() {
    if (this.isOpen) return;

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    document.addEventListener('click', this.#onClickOutside);
    this.addEventListener('keyup', this.#onKeyUp);
  }

  close = () => {
    if (!this.isOpen) return;

    this.refs.panel.setAttribute('hidden', '');
    this.refs.button.setAttribute('aria-expanded', 'false');

    document.removeEventListener('click', this.#onClickOutside);
    this.removeEventListener('keyup', this.#onKeyUp);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    // The click listener lives on the document, so it outlives the element unless it is
    // taken off here — the header is re-rendered on section edits.
    document.removeEventListener('click', this.#onClickOutside);
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /** @param {MouseEvent} event */
  #onClickOutside = (event) => {
    if (isClickedOutside(event, this)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.close();
    event.stopPropagation();
    this.refs.button.focus();
  };
}

if (!customElements.get('language-selector-component')) {
  customElements.define('language-selector-component', LanguageSelectorComponent);
}
